import { apiBuilder, prefixApi } from "@zodios/core";
import { z } from "zod";

// Mirrors api.views.programs.ProgramSerializer (all model fields but deleted_on/deleted_by).

const programEngineSchema = z.enum(["pricing", "bidding", "harvesting", "budget", "lineitem_bidding", "tagging"]);
export type ProgramEngine = z.infer<typeof programEngineSchema>;

const programStatusSchema = z.enum(["scheduled", "active", "paused", "finished", "cancelled", "error"]);
export type ProgramStatus = z.infer<typeof programStatusSchema>;

const dateTimeSchema = z.string().datetime({ offset: true });

const programSchema = z.object({
  id: z.number(),
  // OrganizationSerializer; only the fields the programs UI reads.
  org: z.object({ id: z.number(), name: z.string() }),
  name: z.string(),
  engine: programEngineSchema,
  spec: z.record(z.unknown()),
  ui_state: z.record(z.unknown()),
  template_type: z.string().nullable(),
  status: programStatusSchema,
  start_date: dateTimeSchema,
  end_date: dateTimeSchema.nullable(),
  last_run: dateTimeSchema.nullable(),
  last_action: dateTimeSchema.nullable(),
  run_cnt: z.number(),
  error_reason: z.string().nullable(),
  created_by: z.number(),
  created_on: dateTimeSchema,
  updated_by: z.number(),
  updated_on: dateTimeSchema,
});
export type Program = z.infer<typeof programSchema>;

// The writable ProgramSerializer fields the program forms send. Dates are the form's own strings.
const programWriteSchema = z.object({
  name: z.string(),
  start_date: z.string(),
  end_date: z.string().nullable().optional(),
  ui_state: z.record(z.unknown()),
  spec: z.record(z.unknown()),
  // ProgramContext's engine is a plain string; ProgramSerializer validates the choice.
  engine: z.string(),
  status: programStatusSchema,
});
export type ProgramWrite = z.infer<typeof programWriteSchema>;

export const programsApiDefinitions = prefixApi(
  "/api/programs",
  apiBuilder({
    alias: "getPrograms",
    method: "get",
    path: "/",
    parameters: [
      { name: "engine", type: "Query", schema: programEngineSchema.optional() },
      { name: "pricing_plan", type: "Query", schema: z.number().optional() },
      { name: "all_user_orgs", type: "Query", schema: z.boolean().optional() },
    ],
    status: 200,
    response: z.object({
      count: z.number(),
      next: z.string().nullable(),
      previous: z.string().nullable(),
      results: z.array(programSchema),
    }),
  })
    .addEndpoint({
      alias: "getProgram",
      method: "get",
      path: "/:id/",
      // Retrieve already reaches every org the user manages; all_user_orgs only affects the list.
      status: 200,
      response: programSchema,
    })
    .addEndpoint({
      alias: "createProgram",
      method: "post",
      path: "/",
      parameters: [
        {
          name: "body",
          type: "Body",
          // CreateProgramSerializer: pricing_plan attaches the program to that plan.
          schema: programWriteSchema.extend({
            template_type: z.string(),
            pricing_plan: z.number().optional(),
          }),
        },
      ],
      status: 201,
      response: programSchema,
    })
    .addEndpoint({
      alias: "updateProgram",
      method: "patch",
      path: "/:id/",
      parameters: [{ name: "body", type: "Body", schema: programWriteSchema.partial() }],
      status: 200,
      response: programSchema,
    })
    .addEndpoint({
      alias: "deleteProgram",
      method: "delete",
      path: "/:id/",
      status: 204,
      response: z.void(),
    })
    .build(),
);
